import { useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogTitle, IconButton, MenuItem, TextField } from '@mui/material'
import { ArrowBackIosNew, Close } from '@mui/icons-material'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import useFetch from '../../hooks/useFetch'
import useAuth from '../../hooks/useAuth'
import { urlApi } from '../../config'
import LoadingIndicator from '../LoadingIndicator'
import {
  cleanParam,
  missingParams,
  prefillValues,
  renderTemplate,
  sendErrorMessage,
  whatsappLink
} from '../../utils/whatsappTemplates'

const MUTED = 'text-gray-500 dark:text-gray-400'

const AUDIENCE_LABELS = {
  conductor: 'Conductor',
  pasajero: 'Pasajero',
  todos: 'Todos'
}

// Se recuerda desde qué número mandó cada persona la última vez.
const SENDER_STORAGE_KEY = 'viatik-admin-whatsapp-sender'

const readStoredSender = () => {
  try {
    return window.localStorage.getItem(SENDER_STORAGE_KEY)
  } catch (e) {
    return null
  }
}

const storeSender = key => {
  try {
    window.localStorage.setItem(SENDER_STORAGE_KEY, key)
  } catch (e) {}
}

const describeSender = sender => (sender.phone ? `${sender.label} · ${sender.phone}` : sender.label)

// user: { user_id, first_name, phone_number }. lobby (opcional) completa los datos del viaje.
export default function WhatsAppComposer({ open, onClose, user, lobby }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      {open && <Composer onClose={onClose} user={user} lobby={lobby} />}
    </Dialog>
  )
}

function Composer({ onClose, user, lobby }) {
  const { loading, data, responseOk } = useFetch(`${urlApi}/admin/whatsapp/templates`)
  const [selected, setSelected] = useState(null)
  const [senderKey, setSenderKey] = useState(readStoredSender)
  const templates = responseOk ? data?.templates || [] : []
  const senders = responseOk ? data?.senders || [] : []
  const sender = senders.find(s => s.key === senderKey) || senders[0] || null

  const chooseSender = key => {
    setSenderKey(key)
    storeSender(key)
  }

  return (
    <>
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <div className='flex items-center gap-2'>
          {selected && (
            <IconButton size='small' onClick={() => setSelected(null)} aria-label='Volver'>
              <ArrowBackIosNew fontSize='small' />
            </IconButton>
          )}
          <WhatsAppIcon className='text-green-600 dark:text-emerald-400' />
          <span>{selected ? selected.label : 'Mensaje por WhatsApp'}</span>
        </div>
        <p className={`text-sm font-normal mt-1 ${MUTED}`}>
          Para {user?.first_name || 'el usuario'} · {user?.phone_number || 'sin teléfono'}
        </p>
        <IconButton
          onClick={onClose}
          aria-label='Cerrar'
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div className='flex justify-center py-10'>
            <LoadingIndicator />
          </div>
        ) : !responseOk ? (
          <div className='flex flex-col gap-3 py-6'>
            <p className='text-error'>No se pudieron cargar las plantillas.</p>
            <OwnWhatsAppLink phoneNumber={user?.phone_number} />
          </div>
        ) : selected ? (
          <TemplateForm
            key={selected.key}
            template={selected}
            user={user}
            lobby={lobby}
            senders={senders}
            sender={sender}
            onSenderChange={chooseSender}
            onDone={onClose}
          />
        ) : (
          <TemplateList templates={templates} onSelect={setSelected} user={user} />
        )}
      </DialogContent>
    </>
  )
}

function TemplateList({ templates, onSelect, user }) {
  if (templates.length === 0) {
    return (
      <div className='flex flex-col gap-3 py-6'>
        <p className={MUTED}>No hay plantillas cargadas.</p>
        <OwnWhatsAppLink phoneNumber={user?.phone_number} />
      </div>
    )
  }
  return (
    <ul className='flex flex-col gap-2 pb-2'>
      {templates.map(template => (
        <li key={template.key}>
          <button
            type='button'
            onClick={() => onSelect(template)}
            className='w-full text-left rounded-lg px-4 py-3 ring-1 ring-gray-200 dark:ring-dk-border hover:bg-gray-50 dark:hover:bg-dk-hover transition-colors'
          >
            <span className='flex items-center justify-between gap-3'>
              <span className='font-semibold text-gray-800 dark:text-gray-100'>
                {template.label}
              </span>
              {AUDIENCE_LABELS[template.audience] && (
                <span className='text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-dk-raised dark:text-gray-300'>
                  {AUDIENCE_LABELS[template.audience]}
                </span>
              )}
            </span>
            {template.description && (
              <span className={`block text-sm mt-1 ${MUTED}`}>{template.description}</span>
            )}
          </button>
        </li>
      ))}
    </ul>
  )
}

function TemplateForm({ template, user, lobby, senders, sender, onSenderChange, onDone }) {
  const { getAccessTokenSilently } = useAuth()
  const [values, setValues] = useState(() => prefillValues(template, { user, lobby }))
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(null)

  const preview = useMemo(() => renderTemplate(template, values), [template, values])
  const missing = missingParams(template, values)
  const tooLong = template.params.some(p => cleanParam(values[p.key]).length > p.maxLength)

  const send = async () => {
    setSending(true)
    setError('')
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${urlApi}/admin/whatsapp/send`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(typeof token === 'string' && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({
          user_id: user.user_id,
          template: template.key,
          params: values,
          sender: sender.key
        })
      })
      const body = await res.json().catch(() => ({}))
      if (res.ok && body.ok) {
        setSent({ text: body.text || preview, from: sender.label })
      } else {
        setError(sendErrorMessage(body))
      }
    } catch (e) {
      setError(sendErrorMessage())
    } finally {
      setSending(false)
    }
  }

  if (sent) {
    return (
      <div className='flex flex-col gap-4 pb-2'>
        <p className='font-semibold text-green-700 dark:text-emerald-400'>
          Mensaje enviado desde {sent.from}.
        </p>
        <MessageBubble text={sent.text} />
        <div className='flex justify-end'>
          <button
            type='button'
            className='px-4 py-2 rounded-lg bg-light_gray dark:bg-dk-raised text-black dark:text-gray-100'
            onClick={onDone}
          >
            Cerrar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-4 pb-2'>
      {template.description && <p className={`text-sm ${MUTED}`}>{template.description}</p>}
      <SenderPicker senders={senders} sender={sender} onChange={onSenderChange} />
      {template.params.map(param => {
        const length = cleanParam(values[param.key]).length
        return (
          <TextField
            key={param.key}
            label={param.label}
            placeholder={param.placeholder}
            value={values[param.key] || ''}
            onChange={e => setValues(prev => ({ ...prev, [param.key]: e.target.value }))}
            multiline={Boolean(param.multiline)}
            minRows={param.multiline ? 3 : undefined}
            size='small'
            fullWidth
            error={length > param.maxLength}
            helperText={param.multiline || length > param.maxLength ? `${length}/${param.maxLength}` : ' '}
          />
        )
      })}

      <div>
        <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${MUTED}`}>
          Vista previa
        </p>
        <MessageBubble text={preview} />
      </div>

      {error && (
        <p className='text-sm text-error bg-red-50 dark:bg-red-500/10 rounded-lg px-3 py-2'>
          {error}
        </p>
      )}

      <div className='flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3'>
        <OwnWhatsAppLink phoneNumber={user?.phone_number} text={preview} />
        <button
          type='button'
          onClick={send}
          disabled={sending || !sender || missing.length > 0 || tooLong}
          className='flex items-center justify-center gap-2 px-5 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-50'
        >
          <WhatsAppIcon fontSize='small' />
          {sending ? 'Enviando...' : 'Enviar'}
        </button>
      </div>
    </div>
  )
}

// Salida para escribirle igual desde el WhatsApp propio cuando no hay plantillas para mandar.
function OwnWhatsAppLink({ phoneNumber, text }) {
  if (!phoneNumber) return null
  return (
    <a
      href={whatsappLink(phoneNumber, text)}
      target='_blank'
      rel='noreferrer'
      className={`text-sm underline ${MUTED}`}
    >
      Abrir en mi WhatsApp
    </a>
  )
}

function SenderPicker({ senders, sender, onChange }) {
  if (!sender) {
    return (
      <p className='text-sm text-error'>No hay ningún número de WhatsApp configurado para enviar.</p>
    )
  }
  if (senders.length === 1) {
    return <p className={`text-sm ${MUTED}`}>Sale desde {describeSender(sender)}</p>
  }
  return (
    <TextField
      select
      size='small'
      fullWidth
      label='Desde'
      value={sender.key}
      onChange={e => onChange(e.target.value)}
    >
      {senders.map(option => (
        <MenuItem key={option.key} value={option.key}>
          {describeSender(option)}
        </MenuItem>
      ))}
    </TextField>
  )
}

function MessageBubble({ text }) {
  return (
    <div className='rounded-lg rounded-tl-none px-4 py-3 bg-green-50 text-gray-800 ring-1 ring-green-100 dark:bg-emerald-500/10 dark:text-gray-100 dark:ring-emerald-500/20 whitespace-pre-wrap text-sm leading-relaxed'>
      {text}
    </div>
  )
}
